/** Shared helpers for PropertyDetailsV2 section components. */

export function getDocumentListLabel(d) {
  if (!d) return 'Document';
  const custom = typeof d.displayName === 'string' ? d.displayName.trim() : '';
  if (custom) return custom;
  return d.name || d.fileName || 'Document';
}

export function getDocumentStorageFileName(d) {
  if (!d) return 'document.pdf';
  return d.fileName || d.name || `${d.type || 'document'}.pdf`;
}

const DOCUMENT_TYPE_LABELS = {
  gas: 'Gas Safety Certificate',
  epc: 'EPC Certificate',
  eicr: 'EICR Certificate',
  propertyLicense: 'Property License',
  propertyOwnership: 'Property Ownership',
  howToRent: 'How to Rent Guide',
  protectionScheme: 'Protection Scheme',
  inspections: 'Inventory and Inspections',
  inventory: 'Inventory Report',
  insurance: 'Insurance Document',
  tenancyAgreement: 'Tenancy Agreement',
  maintenance: 'Maintenance Report',
  other: 'Other Document',
  rentToRent: 'Rent to Rent Agreement',
  depositCertificate: 'Deposit Certificate',
  depositDate: 'Deposit Date Document',
  document: 'Document',
};

export function getDocumentTypeLabel(doc) {
  if (!doc) return 'Document';
  const rawType = (doc.type || doc.documentType || 'document').toString().trim();
  if (rawType === 'inventory' || rawType === 'inspections') {
    if (doc.inventorySubcategory === 'inspection') return 'Inspection';
    if (doc.inventorySubcategory === 'inventory') return 'Inventory';
    if (rawType === 'inspections') return DOCUMENT_TYPE_LABELS.inspections;
    return DOCUMENT_TYPE_LABELS.inventory;
  }
  if (DOCUMENT_TYPE_LABELS[rawType]) return DOCUMENT_TYPE_LABELS[rawType];
  return rawType
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Document';
}

export const recordBankHeadCellClass = (isDarkMode) =>
  `text-left text-sm font-medium py-3 px-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`;

export const recordBankBodyCellClass = (isDarkMode, variant = 'default') => {
  const base = 'py-3 px-4 align-middle';
  if (variant === 'muted') {
    return `${base} text-sm whitespace-nowrap ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`;
  }
  if (variant === 'name') {
    return `${base} text-sm min-w-0 ${isDarkMode ? 'text-white' : 'text-gray-900'}`;
  }
  return `${base} text-sm whitespace-nowrap ${isDarkMode ? 'text-white' : 'text-gray-900'}`;
};

export function formatCurrency(amount) {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function formatDate(timestamp) {
  if (!timestamp) return 'No date set';
  try {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch (error) {
    console.error('Error formatting date:', error);
    return 'Invalid date';
  }
}

const DAY = 86400000;
const toDate = (v) => {
  if (!v) return null;
  const d = v?.toDate ? v.toDate() : new Date(v);
  return isNaN(d.getTime()) ? null : d;
};
const daysUntil = (d, now) => Math.ceil((d.getTime() - now.getTime()) / DAY);
const shortDate = (d) => d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const NO_EXPIRY_CERTS = ['howToRent', 'propertyOwnership', 'inspections'];
export const OPEN_MAINTENANCE = ['pending', 'scheduled', 'in_progress', 'in-progress', 'new', 'open'];
export const isOpenJob = (m) => OPEN_MAINTENANCE.includes((m?.status || 'pending').toLowerCase());
export const hasContractor = (m) => !!(
  m?.assignedTo
  || m?.assignedContractor?.contractorId
  || m?.assignedContractor?.contractorName
  || m?.assignedContractor?.contractorEmail
);

/** One compliance row for the property record: tone is 'ok' | 'soon' | 'urgent'. */
export function certificateStatus(cert, now = new Date()) {
  const expiry = toDate(cert?.expiryDate);
  const date = expiry ? shortDate(expiry) : '—';
  if (cert?.notRequired) return { tone: 'ok', date, status: 'Not required' };
  if (cert?.id === 'propertyLicense' && ['landlordInformed', 'applicationInProgress'].includes(cert.propertyLicenseStatus)) {
    return { tone: 'soon', date, status: 'In the works' };
  }
  if (!cert?.url) return { tone: 'urgent', date, status: 'Missing' };
  if (!expiry) {
    return NO_EXPIRY_CERTS.includes(cert.id) ? { tone: 'ok', date, status: 'On file' } : { tone: 'soon', date, status: 'No expiry date set' };
  }
  const days = daysUntil(expiry, now);
  if (days < 0) return { tone: 'urgent', date, days, status: `Expired ${-days} day${days === -1 ? '' : 's'} ago` };
  if (days <= 30) return { tone: 'soon', date, days, status: `Expires in ${days} day${days === 1 ? '' : 's'}` };
  return { tone: 'ok', date, days, status: 'Valid' };
}

/** Jobs without a contractor, then expired/missing certs, then certs expiring within 30 days. */
export function buildAttention({ certificates = [], maintenanceRequests = [], now = new Date() }) {
  const jobs = maintenanceRequests
    .filter((m) => isOpenJob(m) && !hasContractor(m))
    .map((m) => {
      const reported = toDate(m.createdAt);
      const open = reported ? Math.max(0, Math.floor((now - reported) / DAY)) : null;
      return {
        key: `job-${m.id}`,
        kind: 'job',
        rank: 0,
        title: `${m.title || 'Repair'} — no contractor yet`,
        meta: [reported && `Reported ${shortDate(reported)}`, open != null && `${open} day${open === 1 ? '' : 's'} open`].filter(Boolean).join(' · '),
        action: 'Assign contractor',
        request: m,
      };
    });
  const certs = certificates
    .filter((c) => c && c.id !== 'inspections')
    .map((c) => ({ c, s: certificateStatus(c, now) }))
    .filter(({ s }) => s.tone !== 'ok')
    .sort((a, b) => (a.s.tone === b.s.tone ? (a.s.days ?? -1e9) - (b.s.days ?? -1e9) : a.s.tone === 'urgent' ? -1 : 1))
    .map(({ c, s }) => ({
      key: `cert-${c.id}`,
      kind: 'cert',
      rank: s.tone === 'urgent' ? 1 : 2,
      title: s.days == null ? `${c.name}: ${s.status.toLowerCase()}` : `${c.name} ${s.days < 0 ? 'expired' : 'expires'} ${s.date}`,
      meta: s.days == null ? 'Compliance' : s.status,
      action: s.status === 'Missing' ? 'Upload' : s.days == null ? 'Update' : 'Renew',
      cert: c,
    }));
  return [...jobs, ...certs].sort((a, b) => a.rank - b.rank);
}

/** Progress bar steps for an open job; `current` marks the first step not yet done. */
export function jobSteps(m) {
  const status = (m?.status || '').toLowerCase();
  const reported = toDate(m?.createdAt);
  const booked = toDate(m?.estimatedDate);
  const steps = [
    { label: 'Reported', date: reported ? shortDate(reported) : '—', done: true },
    { label: 'Contractor', date: m?.assignedTo || m?.assignedContractor?.contractorName || 'waiting', done: hasContractor(m) },
    { label: 'Booked', date: booked ? shortDate(booked) : '—', done: !!booked || ['scheduled', 'in_progress', 'in-progress'].includes(status) },
    { label: 'In progress', date: '—', done: ['in_progress', 'in-progress'].includes(status) },
    { label: 'Fixed', date: '—', done: status === 'completed' },
  ];
  const i = steps.findIndex((s) => !s.done);
  return steps.map((s, idx) => ({ ...s, current: idx === i }));
}

const moneyAmount = (v) => (typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[£,\s]/g, '')) || 0);
const ordinal = (n) => `${n}${[11, 12, 13].includes(n % 100) ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] || 'th'}`;

/** Rent, due day, deposit and tenancy dates from the current tenant's record. */
export function rentSummary(tenant, now = new Date()) {
  const rent = moneyAmount(tenant?.rentAmount);
  const dueDay = parseInt(tenant?.rentDueDay, 10);
  let nextDue = null;
  if (dueDay >= 1 && dueDay <= 31) {
    const onDay = (y, m) => new Date(y, m, Math.min(dueDay, new Date(y, m + 1, 0).getDate()));
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    nextDue = onDay(now.getFullYear(), now.getMonth());
    if (nextDue < today) nextDue = onDay(now.getFullYear(), now.getMonth() + 1);
  }
  const deposit = moneyAmount(tenant?.depositAmount);
  const scheme = tenant?.depositScheme === 'other' ? tenant?.depositSchemeOther : tenant?.depositScheme;
  const start = toDate(tenant?.tenancyStartDate);
  const end = toDate(tenant?.tenancyEndDate);
  const monthYear = (d) => d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
  return {
    rent,
    dueLabel: nextDue ? `Due on the ${ordinal(dueDay)} · next ${shortDate(nextDue)}` : 'No rent due day set',
    nextDue,
    deposit: deposit ? [formatCurrency(deposit), scheme].filter(Boolean).join(' · ') : 'None recorded',
    tenancy: start || end ? `${start ? monthYear(start) : '—'} – ${end ? monthYear(end) : 'periodic'}` : 'No dates set',
  };
}

export function formatDateTime(timestamp) {
  if (!timestamp) return 'No date set';
  try {
    const date = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch (error) {
    console.error('Error formatting date/time:', error);
    return 'Invalid date';
  }
}
